import React, { useState, useRef, useCallback } from 'react';
import {
  Upload, FileSpreadsheet, FileText, X, Check, AlertTriangle,
  Download, ChevronDown, ChevronRight, Users, Paperclip, Trash2, Eye,
  ArrowRight, Loader,
} from 'lucide-react';
import {
  neuShadow, neuShadowSm, neuShadowInset, btnPrimary, btnSm, btnDanger,
  inp, cardWrap, Badge, CONTACT_TYPES, DOC_TYPES,
} from './crm/crmShared';
import { matchesNameQuery } from '../lib/nameSearch';

// ─── Constants ───────────────────────────────────────────────────────────────

const ACCEPTED_CSV = '.csv,.tsv,.txt';
const ACCEPTED_DOCS = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.rtf,.zip';
const MAX_FILE_SIZE_MB = 25;

const FIELD_OPTIONS = [
  { value: '', label: '-- Skip --' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'contact_type', label: 'Contact Type' },
  { value: 'organization', label: 'Organization' },
  { value: 'title', label: 'Title' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'Zip' },
  { value: 'address', label: 'Address' },
  { value: 'fax', label: 'Fax' },
  { value: 'referral_source', label: 'Referral Source' },
  { value: 'pipeline_stage', label: 'Pipeline Stage' },
  { value: 'tags', label: 'Tags' },
  { value: 'notes', label: 'Notes' },
];

// Best-effort auto-mapping from CSV header to CRM field.
// Covers standard column names plus GoHighLevel export format.
function guessField(header) {
  const h = header.toLowerCase().replace(/[^a-z0-9]/g, '');
  const map = {
    // ── Name fields ─────────────────────────────────────────────────────
    firstname: 'first_name', first: 'first_name', fname: 'first_name',
    givenname: 'first_name', contactfirstname: 'first_name',
    lastname: 'last_name', last: 'last_name', lname: 'last_name',
    surname: 'last_name', familyname: 'last_name', contactlastname: 'last_name',
    name: 'first_name', fullname: 'first_name', contactname: 'first_name',
    // ── Contact info ────────────────────────────────────────────────────
    email: 'email', emailaddress: 'email', mail: 'email',
    contactemail: 'email', primaryemail: 'email',
    phone: 'phone', phonenumber: 'phone', mobile: 'phone', cell: 'phone',
    telephone: 'phone', contactphone: 'phone', primaryphone: 'phone',
    mobilephone: 'phone', cellphone: 'phone', workphone: 'phone',
    // ── Type / Category ─────────────────────────────────────────────────
    type: 'contact_type', contacttype: 'contact_type', category: 'contact_type',
    leadtype: 'contact_type',
    // ── Organization ────────────────────────────────────────────────────
    company: 'organization', organization: 'organization', org: 'organization',
    companyname: 'organization', business: 'organization', employer: 'organization',
    businessname: 'organization', account: 'organization',
    // ── Title ───────────────────────────────────────────────────────────
    title: 'title', jobtitle: 'title', position: 'title', role: 'title',
    // ── Address fields ──────────────────────────────────────────────────
    city: 'city', town: 'city', locality: 'city',
    state: 'state', province: 'state', region: 'state', stateregion: 'state',
    zip: 'zip', zipcode: 'zip', postalcode: 'zip', postal: 'zip', postcode: 'zip',
    address: 'address', street: 'address', streetaddress: 'address',
    address1: 'address', addressline1: 'address', mailingaddress: 'address',
    fulladdress: 'address',
    fax: 'fax', faxnumber: 'fax',
    // ── Source / Pipeline ───────────────────────────────────────────────
    source: 'referral_source', referralsource: 'referral_source',
    leadsource: 'referral_source', leadorigin: 'referral_source',
    contactsource: 'referral_source', medium: 'referral_source',
    stage: 'pipeline_stage', pipelinestage: 'pipeline_stage',
    status: 'pipeline_stage', leadstatus: 'pipeline_stage',
    contactstatus: 'pipeline_stage', pipelinename: 'pipeline_stage',
    opportunitystatus: 'pipeline_stage',
    // ── Tags / Notes ────────────────────────────────────────────────────
    tags: 'tags', tag: 'tags', labels: 'tags', contacttags: 'tags',
    notes: 'notes', note: 'notes', comments: 'notes', description: 'notes',
    additionalinfo: 'notes', contactnotes: 'notes',
    // ── GHL-specific export columns ─────────────────────────────────────
    contactid: '', // skip GHL internal ID
    id: '',        // skip internal ID
    locationid: '', // skip GHL location
    dateadded: 'notes', datecreated: 'notes',
    dateupdated: '', lastupdated: '',
    dnd: '', donotdisturb: '',
    assignedto: '', assigneduser: '',
    country: 'notes', countrycode: '',
    timezone: '', website: 'notes',
    dateofbirth: 'notes', dob: 'notes', birthday: 'notes',
    gender: 'notes',
    ssn: '', socialsecuritynumber: '',
  };
  return map[h] || '';
}

function parseCSV(text, delimiter) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  const sep = delimiter || (text.includes('\t') ? '\t' : ',');
  const parseRow = (line) => {
    const fields = []; let current = ''; let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' && (i === 0 || line[i - 1] !== '\\')) { inQuotes = !inQuotes; }
      else if (ch === sep && !inQuotes) { fields.push(current.trim()); current = ''; }
      else { current += ch; }
    }
    fields.push(current.trim());
    return fields.map(f => f.replace(/^"|"$/g, '').replace(/""/g, '"'));
  };
  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(parseRow).filter(r => r.some(c => c));
  return { headers, rows };
}

// ─── Drop Zone Component ─────────────────────────────────────────────────────

function DropZone({ accept, onFiles, label, icon: Icon, multiple }) {
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) onFiles(files);
  }, [onFiles]);
  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => fileRef.current?.click()}
      style={{
        border: `2px dashed ${dragging ? '#2E8B98' : '#CED4DA'}`,
        borderRadius: 14, padding: '40px 24px', textAlign: 'center',
        cursor: 'pointer', transition: 'all 0.2s ease',
        background: dragging ? '#F0FBFF' : '#FAFBFC',
        boxShadow: dragging ? 'inset 0 0 20px rgba(46,139,152,0.08)' : 'none',
      }}
    >
      <input ref={fileRef} type="file" accept={accept} multiple={multiple}
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files.length > 0) onFiles(Array.from(e.target.files)); e.target.value = ''; }} />
      <Icon size={36} color={dragging ? '#2E8B98' : '#ADB5BD'} style={{ marginBottom: 12 }} />
      <div style={{ fontSize: 14, fontWeight: 600, color: '#495057', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#ADB5BD' }}>or click to browse your computer</div>
    </div>
  );
}

// ─── Main Page Component ─────────────────────────────────────────────────────

export default function CRMImportPage({ user }) {
  // Tab state
  const [activeTab, setActiveTab] = useState('csv');

  // CSV import state
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState(null);       // { headers, rows }
  const [fieldMapping, setFieldMapping] = useState({}); // col index → field name
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(true);

  // Document upload state
  const [docFiles, setDocFiles] = useState([]);        // [{file, contactId, docType, title}]
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [uploadResults, setUploadResults] = useState([]);
  const [uploading, setUploading] = useState(false);

  // ── CSV Handling ─────────────────────────────────────────────────────────

  const handleCSVFile = useCallback((files) => {
    const file = files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      alert(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }
    setCsvFile(file);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCSV(text);
      setCsvData(parsed);
      // Auto-map headers
      const mapping = {};
      parsed.headers.forEach((h, i) => { mapping[i] = guessField(h); });
      setFieldMapping(mapping);
    };
    reader.readAsText(file);
  }, []);

  const handleImport = useCallback(async () => {
    if (!csvData || csvData.rows.length === 0) return;
    setImporting(true);
    setImportResult(null);
    try {
      // Build contacts array from mapped fields
      const contacts = csvData.rows.map(row => {
        const obj = {};
        Object.entries(fieldMapping).forEach(([colIdx, field]) => {
          if (field && row[colIdx] !== undefined) {
            obj[field] = row[colIdx];
          }
        });
        return obj;
      }).filter(c => c.first_name || c.last_name || c.email || c.phone);

      if (contacts.length === 0) {
        alert('No valid contacts found. Please map at least a name, email, or phone field.');
        setImporting(false);
        return;
      }

      const res = await window.zignagenixAPI.bulkImport({ contacts });
      setImportResult({ ...res, total: contacts.length });
    } catch (err) {
      setImportResult({ error: true, message: err.message || 'Import failed' });
    }
    setImporting(false);
  }, [csvData, fieldMapping]);

  const resetCSV = () => {
    setCsvFile(null); setCsvData(null); setFieldMapping({}); setImportResult(null);
  };

  // ── Document Upload Handling ─────────────────────────────────────────────

  const handleDocFiles = useCallback((files) => {
    const newFiles = files.filter(f => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024).map(f => ({
      file: f,
      contactId: '',
      docType: 'general',
      title: f.name.replace(/\.[^.]+$/, ''),
    }));
    if (newFiles.length < files.length) {
      alert(`${files.length - newFiles.length} file(s) exceeded the ${MAX_FILE_SIZE_MB}MB limit and were skipped.`);
    }
    setDocFiles(prev => [...prev, ...newFiles]);
  }, []);

  const updateDocFile = (idx, field, value) => {
    setDocFiles(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };

  const removeDocFile = (idx) => {
    setDocFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDocUpload = useCallback(async () => {
    if (docFiles.length === 0) return;
    setUploading(true);
    const results = [];
    for (const df of docFiles) {
      try {
        // Read file as base64 for web upload
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(df.file);
        });

        const res = await window.zignagenixAPI.createCRMDocument({
          contact_id: df.contactId || null,
          title: df.title,
          file_name: df.file.name,
          file_path: `uploads/crm/${Date.now()}_${df.file.name}`,
          file_size: df.file.size,
          mime_type: df.file.type || 'application/octet-stream',
          doc_type: df.docType,
          file_data: base64,
        });
        results.push({ name: df.file.name, success: true, id: res.documentId });
      } catch (err) {
        results.push({ name: df.file.name, success: false, error: err.message });
      }
    }
    setUploadResults(results);
    setUploading(false);
    // Clear successful uploads
    const failedIndexes = results.map((r, i) => r.success ? -1 : i).filter(i => i >= 0);
    setDocFiles(prev => prev.filter((_, i) => failedIndexes.includes(i)));
  }, [docFiles]);

  // Load contacts for document attachment dropdown
  const loadContacts = useCallback(async () => {
    try {
      const res = await window.zignagenixAPI.getCRMContacts({ limit: 500 });
      setContacts(Array.isArray(res) ? res : res.contacts || []);
    } catch { }
  }, []);

  React.useEffect(() => { loadContacts(); }, [loadContacts]);

  const filteredContacts = contacts.filter(c => {
    return matchesNameQuery([c.first_name, c.last_name, c.email], contactSearch);
  });

  // ── Render ───────────────────────────────────────────────────────────────

  const tabStyle = (active) => ({
    padding: '10px 20px', borderRadius: '10px 10px 0 0', border: 'none',
    cursor: 'pointer', fontSize: 13, fontWeight: 600,
    background: active ? '#F8F9FA' : 'transparent',
    color: active ? '#2E8B98' : '#6C757D',
    boxShadow: active ? neuShadow : 'none',
    transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', gap: 6,
  });

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#212529', margin: 0, marginBottom: 4 }}>
          CRM Import & Upload
        </h1>
        <p style={{ fontSize: 13, color: '#6C757D', margin: 0 }}>
          Import contacts from CSV files or upload documents to attach to CRM records
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 0 }}>
        <button onClick={() => setActiveTab('csv')} style={tabStyle(activeTab === 'csv')}>
          <FileSpreadsheet size={15} /> CSV Contact Import
        </button>
        <button onClick={() => setActiveTab('docs')} style={tabStyle(activeTab === 'docs')}>
          <Paperclip size={15} /> Document Upload
        </button>
      </div>

      {/* Content */}
      <div style={{ ...cardWrap, borderRadius: '0 14px 14px 14px', padding: 24 }}>

        {/* ═══ CSV IMPORT TAB ═══ */}
        {activeTab === 'csv' && (
          <div>
            {!csvFile ? (
              /* Step 1: File Selection */
              <div>
                <DropZone accept={ACCEPTED_CSV} onFiles={handleCSVFile} label="Drop a CSV or TSV file here" icon={FileSpreadsheet} />
                <div style={{ marginTop: 16, padding: '14px 18px', background: '#F0F7FF', borderRadius: 10, fontSize: 12, color: '#495057' }}>
                  <strong>Supported formats:</strong> CSV (.csv), TSV (.tsv), or text files with comma/tab-separated values.
                  The first row should contain column headers (e.g., first_name, last_name, email, phone).
                  <br /><br />
                  <strong>Tip:</strong> Export from GoHighLevel, Salesforce, HubSpot, or Excel and upload here.
                  Fields will be auto-mapped where possible.
                </div>
              </div>
            ) : !importResult ? (
              /* Step 2: Field Mapping & Preview */
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>
                      <FileSpreadsheet size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                      {csvFile.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#6C757D', marginTop: 2 }}>
                      {csvData?.headers?.length || 0} columns, {csvData?.rows?.length || 0} rows detected
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={resetCSV} style={btnSm}><X size={13} /> Start Over</button>
                    <button onClick={handleImport} disabled={importing} style={btnPrimary}>
                      {importing ? <><Loader size={13} className="spin" /> Importing...</> : <><Upload size={13} /> Import {csvData?.rows?.length || 0} Contacts</>}
                    </button>
                  </div>
                </div>

                {/* Field Mapping */}
                <div style={{ ...cardWrap, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 12 }}>
                    Map CSV Columns to CRM Fields
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                    {csvData?.headers?.map((header, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: '1 1 120px', fontSize: 12, color: '#495057', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {header}
                        </div>
                        <ArrowRight size={14} color="#ADB5BD" />
                        <select
                          value={fieldMapping[idx] || ''}
                          onChange={e => setFieldMapping(prev => ({ ...prev, [idx]: e.target.value }))}
                          style={{ ...inp, flex: '1 1 140px', padding: '6px 10px', fontSize: 12 }}
                        >
                          {FIELD_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Data Preview */}
                <div style={cardWrap}>
                  <div
                    onClick={() => setPreviewExpanded(!previewExpanded)}
                    style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#212529' }}
                  >
                    {previewExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Data Preview (first 10 rows)
                  </div>
                  {previewExpanded && (
                    <div className="responsive-scroll" style={{ overflowX: 'auto', maxHeight: 300 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 720 }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '8px 12px', background: '#E9ECEF', textAlign: 'left', color: '#495057', fontWeight: 600, position: 'sticky', top: 0 }}>#</th>
                            {csvData?.headers?.map((h, i) => (
                              <th key={i} style={{ padding: '8px 12px', background: '#E9ECEF', textAlign: 'left', color: fieldMapping[i] ? '#2E8B98' : '#ADB5BD', fontWeight: 600, position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>
                                {fieldMapping[i] ? FIELD_OPTIONS.find(o => o.value === fieldMapping[i])?.label : h}
                                {!fieldMapping[i] && <span style={{ fontSize: 10, color: '#DC3545', marginLeft: 4 }}>skip</span>}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {csvData?.rows?.slice(0, 10).map((row, ri) => (
                            <tr key={ri} style={{ borderBottom: '1px solid #E9ECEF' }}>
                              <td style={{ padding: '6px 12px', color: '#ADB5BD' }}>{ri + 1}</td>
                              {row.map((cell, ci) => (
                                <td key={ci} style={{ padding: '6px 12px', color: fieldMapping[ci] ? '#212529' : '#CED4DA', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {cell || '—'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Step 3: Import Results */
              <div>
                <div style={{
                  padding: 24, borderRadius: 14, textAlign: 'center', marginBottom: 16,
                  background: importResult.error ? '#FEF2F2' : '#F0FFF4',
                  border: `1px solid ${importResult.error ? '#FECACA' : '#BBF7D0'}`,
                }}>
                  {importResult.error ? (
                    <>
                      <AlertTriangle size={36} color="#DC3545" style={{ marginBottom: 8 }} />
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#DC3545' }}>Import Failed</div>
                      <div style={{ fontSize: 13, color: '#6C757D', marginTop: 4 }}>{importResult.message}</div>
                    </>
                  ) : (
                    <>
                      <Check size={36} color="#22C55E" style={{ marginBottom: 8 }} />
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#22C55E' }}>Import Complete</div>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
                        <div><div style={{ fontSize: 24, fontWeight: 700, color: '#22C55E' }}>{importResult.imported || 0}</div><div style={{ fontSize: 11, color: '#6C757D' }}>New</div></div>
                        <div><div style={{ fontSize: 24, fontWeight: 700, color: '#2E8B98' }}>{importResult.updated || 0}</div><div style={{ fontSize: 11, color: '#6C757D' }}>Updated</div></div>
                        <div><div style={{ fontSize: 24, fontWeight: 700, color: '#F59E0B' }}>{importResult.skipped || 0}</div><div style={{ fontSize: 11, color: '#6C757D' }}>Skipped</div></div>
                        {(importResult.failed || 0) > 0 && <div><div style={{ fontSize: 24, fontWeight: 700, color: '#DC3545' }}>{importResult.failed}</div><div style={{ fontSize: 11, color: '#6C757D' }}>Failed</div></div>}
                        <div><div style={{ fontSize: 24, fontWeight: 700, color: '#6C757D' }}>{importResult.total}</div><div style={{ fontSize: 11, color: '#6C757D' }}>Total</div></div>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div style={{ marginTop: 12, fontSize: 12, color: '#DC3545', textAlign: 'left', maxHeight: 120, overflowY: 'auto' }}>
                          {importResult.errors.map((e, i) => <div key={i}>{e.name}: {e.error}</div>)}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <button onClick={resetCSV} style={btnPrimary}><Upload size={13} /> Import Another File</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ DOCUMENT UPLOAD TAB ═══ */}
        {activeTab === 'docs' && (
          <div>
            <DropZone accept={ACCEPTED_DOCS} onFiles={handleDocFiles} multiple
              label="Drop documents here to upload" icon={FileText} />

            {/* File Queue */}
            {docFiles.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>
                    {docFiles.length} file{docFiles.length > 1 ? 's' : ''} ready to upload
                  </div>
                  <button onClick={handleDocUpload} disabled={uploading} style={btnPrimary}>
                    {uploading ? <><Loader size={13} /> Uploading...</> : <><Upload size={13} /> Upload All</>}
                  </button>
                </div>

                {docFiles.map((df, idx) => (
                  <div key={idx} style={{
                    ...cardWrap, padding: 14, marginBottom: 10,
                    display: 'flex', gap: 12, alignItems: 'flex-start',
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: '#F0F7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <FileText size={18} color="#2E8B98" />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {df.file.name}
                        <span style={{ fontSize: 11, color: '#ADB5BD', fontWeight: 400, marginLeft: 8 }}>
                          {(df.file.size / 1024).toFixed(1)} KB
                        </span>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                        <div>
                          <label style={{ fontSize: 11, color: '#6C757D', fontWeight: 500, marginBottom: 4, display: 'block' }}>Title</label>
                          <input
                            value={df.title}
                            onChange={e => updateDocFile(idx, 'title', e.target.value)}
                            style={{ ...inp, padding: '6px 10px', fontSize: 12 }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#6C757D', fontWeight: 500, marginBottom: 4, display: 'block' }}>Document Type</label>
                          <select
                            value={df.docType}
                            onChange={e => updateDocFile(idx, 'docType', e.target.value)}
                            style={{ ...inp, padding: '6px 10px', fontSize: 12 }}
                          >
                            {DOC_TYPES.map(t => (
                              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#6C757D', fontWeight: 500, marginBottom: 4, display: 'block' }}>Link to Contact</label>
                          <select
                            value={df.contactId}
                            onChange={e => updateDocFile(idx, 'contactId', e.target.value)}
                            style={{ ...inp, padding: '6px 10px', fontSize: 12 }}
                          >
                            <option value="">-- No Contact --</option>
                            {filteredContacts.slice(0, 100).map(c => (
                              <option key={c.id} value={c.id}>
                                {c.first_name} {c.last_name} {c.email ? `(${c.email})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <button onClick={() => removeDocFile(idx)} style={{ ...btnSm, padding: 6, color: '#DC3545' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Results */}
            {uploadResults.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#212529', marginBottom: 12 }}>Upload Results</div>
                {uploadResults.map((r, i) => (
                  <div key={i} style={{
                    padding: '10px 14px', borderRadius: 8, marginBottom: 6,
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: r.success ? '#F0FFF4' : '#FEF2F2',
                    border: `1px solid ${r.success ? '#BBF7D0' : '#FECACA'}`,
                  }}>
                    {r.success ? <Check size={14} color="#22C55E" /> : <AlertTriangle size={14} color="#DC3545" />}
                    <span style={{ fontSize: 12, color: '#495057', flex: 1 }}>{r.name}</span>
                    <span style={{ fontSize: 11, color: r.success ? '#22C55E' : '#DC3545' }}>
                      {r.success ? 'Uploaded' : r.error}
                    </span>
                  </div>
                ))}
                <button onClick={() => setUploadResults([])} style={{ ...btnSm, marginTop: 8 }}>
                  <X size={13} /> Clear Results
                </button>
              </div>
            )}

            {/* Help text */}
            {docFiles.length === 0 && uploadResults.length === 0 && (
              <div style={{ marginTop: 16, padding: '14px 18px', background: '#F0F7FF', borderRadius: 10, fontSize: 12, color: '#495057' }}>
                <strong>Supported formats:</strong> PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Images (.png, .jpg, .gif), Text files, ZIP archives.
                <br /><br />
                <strong>Maximum file size:</strong> {MAX_FILE_SIZE_MB}MB per file. You can upload multiple files at once.
                <br /><br />
                <strong>Tip:</strong> Optionally link each document to a CRM contact and set a document type for easy organization.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
